import React, { useState, useEffect } from 'react';
import { Search, Target, Book, Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const TypesConcoursTab = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    niveau_requis: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchTypes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/types-concours/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setTypes(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des types de concours');
      setTypes([]);
      setPagination(null);
      toast.error('Impossible de charger les types de concours');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTypes();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal
  const handleOpenModal = (type = null) => {
    if (type) {
      setSelectedType(type);
      setFormData({
        nom: type.nom || '',
        description: type.description || '',
        niveau_requis: type.niveau_requis || ''
      });
    } else {
      setSelectedType(null);
      setFormData({
        nom: '',
        description: '',
        niveau_requis: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.description.trim()) {
      errors.description = "La description est requise";
    }
    if (!formData.niveau_requis) {
      errors.niveau_requis = "Le niveau requis est requis";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (selectedType) {
        // Mise à jour
        await api.put(`/concours/types-concours/${selectedType.id}/`, formData);
        toast.success("Type de concours mis à jour avec succès");
      } else {
        // Création
        await api.post('/concours/types-concours/', formData);
        toast.success("Type de concours créé avec succès");
      }

      fetchTypes();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (type) => {
    setTypeToDelete(type);
    setConfirmConfig({
      title: "Supprimer le type de concours",
      message: `Êtes-vous sûr de vouloir supprimer le type de concours "${type.nom}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!typeToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/types-concours/${typeToDelete.id}/`);
      toast.success("Type de concours supprimé avec succès");
      fetchTypes();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer ce type de concours. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Rendering du footer du modal
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedType ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  if (loading && types.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher un type de concours..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouveau Type
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {types.length === 0 && !loading ? (
        <EmptyState
          icon={Target}
          title="Aucun type de concours"
          description="Aucun type de concours trouvé."
          actionLabel="Créer un type"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type de concours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveau requis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {types.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Target className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{type.nom}</div>
                          {type.description && (
                            <div className="text-sm text-gray-500">{type.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Book className="h-3 w-3 mr-1" />
                        {type.niveau_requis}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleOpenModal(type)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleOpenDeleteModal(type)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedType ? 'Modifier le type de concours' : 'Nouveau type de concours'}
        footer={renderModalFooter()}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            label="Nom du type"
            type="text"
            name="nom"
            value={formData.nom}
            onChange={handleInputChange}
            placeholder="Ex: Concours d'entrée en Licence"
            required
            error={formErrors.nom}
            icon={Target}
          />
          
          <FormInput
            label="Niveau requis"
            type="select"
            name="niveau_requis"
            value={formData.niveau_requis}
            onChange={handleInputChange}
            options={[
              { value: '', label: 'Sélectionner un niveau' },
              { value: 'BAC', label: 'Baccalauréat' },
              { value: 'LICENCE', label: 'Licence' },
              { value: 'MASTER', label: 'Master' },
              { value: 'AUTRE', label: 'Autre' }
            ]}
            required
            error={formErrors.niveau_requis}
            icon={Book}
          />

          <FormInput
            label="Description"
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description détaillée du type de concours"
            required
            error={formErrors.description}
            rows={4}
          />

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le type de concours"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce type de concours ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default TypesConcoursTab;